import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  Check,
  X,
  Trash2,
  Mail,
  Phone,
  MapPin,
  Gift,
  Download,
  CreditCard,
  Search,
} from "lucide-react";
import { API_BASE_URL } from "../config";
import { InlineSpinner } from "../components/InlineSpinner";
import { CopyableCode } from "../components/CopyableCode";
import { Pagination } from "../components/Pagination";
import { SortOrderToggle, SortOrder } from "../components/SortOrderToggle";
import { usePagination } from "../hooks/usePagination";
import { useConfirm } from "../contexts/ConfirmDialogContext";
import styles from "./CustomOrdersPage.module.css";

const getImageUrl = (imageUrl: string) => {
  if (imageUrl.startsWith("http")) return imageUrl;
  return `${API_BASE_URL}${imageUrl}`;
};

type PaymentMethod = "cod" | "instapay" | "wallet";

interface CustomOrder {
  _id: string;
  referenceNumber: string;
  guestName: string;
  guestEmail: string;
  guestPhone: string;
  imageUrl: string;
  deliveryAddress: string;
  governorate: string;
  area: string;
  paintingType: "portrait" | "landscape" | "others";
  portraitCount: string;
  canvasSize: string;
  price: number;
  originalPrice: number;
  discountPercent: number;
  shippingCost: number;
  specialInstructions: string;
  isGift: boolean;
  giftFee: number;
  giftMessage: string;
  status: "pending" | "confirmed" | "cancelled";
  paymentMethod: PaymentMethod;
  isPaid: boolean;
  createdAt: string;
}

const PORTRAIT_COUNT_LABELS: Record<string, string> = {
  "1": "1 Person",
  "2": "2 People",
  "3": "3 People",
  "4-6": "4-6 People",
  "7-12": "7-12 People",
};

const PAINTING_TYPE_LABELS: Record<string, string> = {
  portrait: "Portrait",
  landscape: "Landscape",
  others: "Others",
};

const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  cod: "Cash on Delivery",
  instapay: "Instapay",
  wallet: "Mobile Wallet",
};

export function CustomOrdersPage() {
  const { token } = useAuth();
  const confirm = useConfirm();
  const [orders, setOrders] = useState<CustomOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState<SortOrder>("newest");
  const [selectedOrder, setSelectedOrder] = useState<CustomOrder | null>(null);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const isBusy = (id: string, action: string) => busyAction === `${id}:${action}`;
  const isRowBusy = (id: string) => !!busyAction && busyAction.startsWith(`${id}:`);

  const fetchOrders = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/custom-orders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setOrders(data.orders || []);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [token]);

  const updateStatus = async (
    id: string,
    status: "confirmed" | "cancelled",
  ) => {
    setBusyAction(`${id}:${status === "confirmed" ? "confirm" : "cancel"}`);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/custom-orders/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ status }),
        },
      );

      if (!response.ok) throw new Error("Failed to update status");
      await fetchOrders();
      setSelectedOrder(null);
    } catch (error) {
      alert("Failed to update order status");
    } finally {
      setBusyAction(null);
    }
  };

  const togglePaid = async (id: string, currentIsPaid: boolean) => {
    setBusyAction(`${id}:paid`);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/custom-orders/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ isPaid: !currentIsPaid }),
        },
      );

      if (!response.ok) throw new Error("Failed to update payment status");
      const data = await response.json();
      setOrders((prev) => prev.map((o) => (o._id === id ? data.order : o)));
      setSelectedOrder((prev) => (prev && prev._id === id ? data.order : prev));
    } catch (error) {
      alert("Failed to update payment status");
    } finally {
      setBusyAction(null);
    }
  };

  const deleteOrder = async (id: string) => {
    const confirmed = await confirm({
      message: "Are you sure you want to delete this order? This cannot be undone.",
      confirmLabel: "Delete",
    });
    if (!confirmed) return;

    setBusyAction(`${id}:delete`);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/custom-orders/${id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (!response.ok) throw new Error("Failed to delete");
      await fetchOrders();
      setSelectedOrder(null);
    } catch (error) {
      alert("Failed to delete order");
    } finally {
      setBusyAction(null);
    }
  };

  const filteredOrders = orders
    .filter((o) => {
      if (filter !== "all" && o.status !== filter) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.trim().toLowerCase();
        return (
          o.referenceNumber?.toLowerCase().includes(q) ||
          o.guestName.toLowerCase().includes(q) ||
          o.guestPhone.includes(q)
        );
      }
      return true;
    })
    .sort((a, b) => {
      const diff = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      return sortOrder === "newest" ? -diff : diff;
    });

  const { page, setPage, pageCount, pageItems, totalItems } = usePagination(filteredOrders, 20);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status: string) => {
    const badgeStyles: Record<string, React.CSSProperties> = {
      pending: { background: "#fef3c7", color: "#92400e" },
      confirmed: { background: "#d1fae5", color: "#065f46" },
      cancelled: { background: "#fee2e2", color: "#991b1b" },
    };
    return badgeStyles[status] || {};
  };

  const getPaidBadgeStyle = (isPaid: boolean): React.CSSProperties =>
    isPaid
      ? { background: "#d1fae5", color: "#065f46" }
      : { background: "#fee2e2", color: "#991b1b" };

  const downloadImage = async (id: string, imageUrl: string, guestName: string) => {
    setBusyAction(`${id}:download`);
    try {
      const fullUrl = getImageUrl(imageUrl);
      const response = await fetch(fullUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      // Get file extension from URL or default to jpg
      const extension = imageUrl.split(".").pop() || "jpg";
      const safeName = guestName.replace(/[^a-z0-9]/gi, "_").toLowerCase();
      link.download = `custom_order_${safeName}.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading image:", error);
      alert("Failed to download image");
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1>Custom Orders</h1>
          <p>Manage custom paint-by-numbers orders</p>
        </div>
        <div className={styles.filters}>
          <div className={styles.searchBox}>
            <Search size={16} />
            <input
              type="text"
              placeholder="Search by reference #, name, or phone"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          {["all", "pending", "confirmed", "cancelled"].map((f) => (
            <button
              key={f}
              className={`${styles.filterBtn} ${filter === f ? styles.filterActive : ""}`}
              onClick={() => setFilter(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
          <SortOrderToggle value={sortOrder} onChange={setSortOrder} />
        </div>
      </header>

      {loading ? (
        <div className={styles.loading}>Loading orders...</div>
      ) : filteredOrders.length === 0 ? (
        <div className={styles.empty}>
          <p>No {filter !== "all" ? filter : ""} custom orders found.</p>
        </div>
      ) : (
        <div className={styles.grid}>
          {pageItems.map((order) => (
            <div
              key={order._id}
              className={styles.card}
              onClick={() => setSelectedOrder(order)}
            >
              <div className={styles.cardImage}>
                <img src={getImageUrl(order.imageUrl)} alt="Custom order" />
                {order.isGift && (
                  <div className={styles.giftBadge}>
                    <Gift size={14} />
                    Gift
                  </div>
                )}
              </div>
              <div className={styles.cardContent}>
                <div className={styles.cardHeader}>
                  <strong>{order.guestName}</strong>
                  <span
                    className={styles.statusBadge}
                    style={getStatusBadge(order.status)}
                  >
                    {order.status}
                  </span>
                </div>
                {order.referenceNumber && (
                  <div onClick={(e) => e.stopPropagation()} style={{ marginBottom: '0.5rem' }}>
                    <CopyableCode value={order.referenceNumber} />
                  </div>
                )}
                <div className={styles.orderDetails}>
                  <span>
                    {order.paintingType === "portrait"
                      ? PORTRAIT_COUNT_LABELS[order.portraitCount] || order.portraitCount
                      : PAINTING_TYPE_LABELS[order.paintingType] || order.paintingType}
                    {" "}&bull; {order.canvasSize} cm
                  </span>
                  <span>EGP {order.price + order.shippingCost}</span>
                </div>
                <div className={styles.orderDetails}>
                  <span>{PAYMENT_METHOD_LABELS[order.paymentMethod] || order.paymentMethod}</span>
                  <span
                    className={styles.statusBadge}
                    style={getPaidBadgeStyle(order.isPaid)}
                  >
                    {order.isPaid ? "Paid" : "Unpaid"}
                  </span>
                </div>
                <p className={styles.date}>{formatDate(order.createdAt)}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && filteredOrders.length > 0 && (
        <Pagination
          page={page}
          pageCount={pageCount}
          totalItems={totalItems}
          onPageChange={setPage}
        />
      )}

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div
          className={styles.modalOverlay}
          onClick={() => setSelectedOrder(null)}
        >
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalTopActions}>
              <button
                className={styles.downloadBtn}
                onClick={() =>
                  downloadImage(selectedOrder._id, selectedOrder.imageUrl, selectedOrder.guestName)
                }
                disabled={isBusy(selectedOrder._id, "download")}
                title="Download Image"
              >
                {isBusy(selectedOrder._id, "download") ? <InlineSpinner size={20} /> : <Download size={20} />}
              </button>
              <button
                className={styles.closeBtn}
                onClick={() => setSelectedOrder(null)}
                title="Close"
              >
                <X size={20} />
              </button>
            </div>

            <div className={styles.modalImage}>
              <img
                src={getImageUrl(selectedOrder.imageUrl)}
                alt="Custom order"
              />
            </div>

            <div className={styles.modalContent}>
              <div className={styles.modalHeader}>
                <div>
                  <h2>Custom Order Details</h2>
                  {selectedOrder.referenceNumber && (
                    <CopyableCode value={selectedOrder.referenceNumber} />
                  )}
                </div>
                <span
                  className={styles.statusBadge}
                  style={getStatusBadge(selectedOrder.status)}
                >
                  {selectedOrder.status}
                </span>
              </div>

              <div className={styles.detailSection}>
                <h3>Customer Information</h3>
                <div className={styles.detailGrid}>
                  <div>
                    <label>Name</label>
                    <p>{selectedOrder.guestName}</p>
                  </div>
                  <div>
                    <label>Email</label>
                    <p>
                      <Mail size={14} /> {selectedOrder.guestEmail}
                    </p>
                  </div>
                  <div>
                    <label>Phone</label>
                    <p>
                      <Phone size={14} /> {selectedOrder.guestPhone}
                    </p>
                  </div>
                  <div>
                    <label>Delivery Address</label>
                    <p>
                      <MapPin size={14} /> {selectedOrder.deliveryAddress}
                    </p>
                  </div>
                </div>
              </div>

              <div className={styles.detailSection}>
                <h3>Order Specifications</h3>
                <div className={styles.detailGrid}>
                  <div>
                    <label>Painting Type</label>
                    <p>{PAINTING_TYPE_LABELS[selectedOrder.paintingType] || selectedOrder.paintingType}</p>
                  </div>
                  {selectedOrder.paintingType === "portrait" && (
                    <div>
                      <label>Number of People</label>
                      <p>{PORTRAIT_COUNT_LABELS[selectedOrder.portraitCount] || selectedOrder.portraitCount}</p>
                    </div>
                  )}
                  <div>
                    <label>Canvas Size</label>
                    <p>{selectedOrder.canvasSize} cm</p>
                  </div>
                  <div>
                    <label>Price</label>
                    <p>
                      EGP {selectedOrder.price}
                      {selectedOrder.discountPercent > 0 && (
                        <> ({selectedOrder.discountPercent}% off EGP {selectedOrder.originalPrice})</>
                      )}
                    </p>
                  </div>
                  <div>
                    <label>Shipping</label>
                    <p>EGP {selectedOrder.shippingCost}</p>
                  </div>
                  <div>
                    <label>Total</label>
                    <p>EGP {selectedOrder.price + selectedOrder.shippingCost}</p>
                  </div>
                </div>
                {selectedOrder.specialInstructions && (
                  <div>
                    <label>Special Instructions</label>
                    <p>{selectedOrder.specialInstructions}</p>
                  </div>
                )}
              </div>

              <div className={styles.detailSection}>
                <h3>
                  <CreditCard size={18} /> Payment
                </h3>
                <div className={styles.detailGrid}>
                  <div>
                    <label>Method</label>
                    <p>{PAYMENT_METHOD_LABELS[selectedOrder.paymentMethod] || selectedOrder.paymentMethod}</p>
                  </div>
                  <div>
                    <label>Status</label>
                    <p>
                      <span className={styles.statusBadge} style={getPaidBadgeStyle(selectedOrder.isPaid)}>
                        {selectedOrder.isPaid ? "Paid" : "Unpaid"}
                      </span>
                    </p>
                  </div>
                </div>
                <button
                  className={selectedOrder.isPaid ? styles.cancelBtn : styles.confirmBtn}
                  style={{ marginTop: "1rem" }}
                  onClick={() => togglePaid(selectedOrder._id, selectedOrder.isPaid)}
                  disabled={isRowBusy(selectedOrder._id)}
                >
                  {isBusy(selectedOrder._id, "paid") ? <InlineSpinner size={18} /> : <CreditCard size={18} />}
                  {selectedOrder.isPaid ? "Mark as Unpaid" : "Mark as Paid"}
                </button>
              </div>

              {selectedOrder.isGift && (
                <div className={styles.detailSection}>
                  <h3>
                    <Gift size={18} /> Gift Order
                  </h3>
                  {selectedOrder.giftFee > 0 && (
                    <p>Gift Fee: EGP {selectedOrder.giftFee}</p>
                  )}
                  {selectedOrder.giftMessage && (
                    <div className={styles.giftMessage}>
                      <label>Gift Message</label>
                      <p>"{selectedOrder.giftMessage}"</p>
                    </div>
                  )}
                </div>
              )}

              <div className={styles.modalActions}>
                {selectedOrder.status === "pending" && (
                  <>
                    <button
                      className={styles.confirmBtn}
                      onClick={() =>
                        updateStatus(selectedOrder._id, "confirmed")
                      }
                      disabled={isRowBusy(selectedOrder._id)}
                    >
                      {isBusy(selectedOrder._id, "confirm") ? <InlineSpinner size={18} /> : <Check size={18} />}
                      Confirm Order
                    </button>
                    <button
                      className={styles.cancelBtn}
                      onClick={() =>
                        updateStatus(selectedOrder._id, "cancelled")
                      }
                      disabled={isRowBusy(selectedOrder._id)}
                    >
                      {isBusy(selectedOrder._id, "cancel") ? <InlineSpinner size={18} /> : <X size={18} />}
                      Cancel Order
                    </button>
                  </>
                )}
                <button
                  className={styles.deleteBtn}
                  onClick={() => deleteOrder(selectedOrder._id)}
                  disabled={isRowBusy(selectedOrder._id)}
                >
                  {isBusy(selectedOrder._id, "delete") ? <InlineSpinner size={18} /> : <Trash2 size={18} />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
